/**
 * Searchable CPT/HCPCS picker backed by the CMS PFS ValueSet.
 */
import { Autocomplete, Text } from '@mantine/core';
import { IconReceipt } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import {
  CMS_PFS_DROPDOWN_LIMIT,
  cmsPfsOptionLabel,
  filterCmsPfsProcedureCodes,
  loadCmsPfsProcedureCodes,
  type CmsPfsProcedureCode,
} from '@/pages/calendar/cmsPfsProcedureCodes';

export interface ProcedureCodeSelectProps {
  code: string;
  title: string;
  onSelect: (concept: CmsPfsProcedureCode) => void;
  required?: boolean;
  disabled?: boolean;
  label?: string;
}

const DROPDOWN_WIDTH_PX = 520;

export function ProcedureCodeSelect({
  code,
  title,
  onSelect,
  required,
  disabled,
  label = 'CPT / HCPCS code',
}: ProcedureCodeSelectProps): JSX.Element {
  const medplum = useMedplum();
  const [inputValue, setInputValue] = useState(code);
  const [concepts, setConcepts] = useState<CmsPfsProcedureCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    setInputValue(code);
  }, [code]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void loadCmsPfsProcedureCodes(medplum)
      .then((loaded) => {
        if (cancelled) return;
        setConcepts(loaded);
        setError(undefined);
      })
      .catch(() => {
        if (cancelled) return;
        setConcepts([]);
        setError('Could not load procedure codes. Check that the CMS PFS ValueSet is imported and readable.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum]);

  const options = useMemo(
    () => filterCmsPfsProcedureCodes(concepts, inputValue),
    [concepts, inputValue],
  );

  const selectedInOptions = options.some((option) => option.code === code.trim().toUpperCase());
  const data = useMemo(() => {
    const items = options.map((concept) => ({
      value: concept.code,
      label: cmsPfsOptionLabel(concept),
    }));
    if (!code.trim() || selectedInOptions) return items;
    return [
      {
        value: code.trim().toUpperCase(),
        label: `${code.trim().toUpperCase()} – ${title.trim() || 'Previously selected (not in current PFS list)'}`,
      },
      ...items.slice(0, CMS_PFS_DROPDOWN_LIMIT - 1),
    ];
  }, [code, options, selectedInOptions, title]);

  const handleOptionSubmit = (value: string) => {
    const selected = options.find((option) => option.code === value);
    setInputValue(value);
    onSelect(selected ?? { code: value, display: title.trim() || value });
  };

  return (
    <Autocomplete
      label={label}
      placeholder="Search CPT or HCPCS codes…"
      value={inputValue}
      onChange={setInputValue}
      onOptionSubmit={handleOptionSubmit}
      data={data}
      clearable
      required={required}
      limit={CMS_PFS_DROPDOWN_LIMIT}
      maxDropdownHeight={320}
      leftSection={<IconReceipt size={14} />}
      comboboxProps={{ withinPortal: true, width: DROPDOWN_WIDTH_PX }}
      error={error}
      disabled={disabled || loading}
      renderOption={({ option }) => {
        const labeled = option as { value: string; label?: string };
        const fullLabel = labeled.label || labeled.value;
        return (
          <Text size="sm" style={{ whiteSpace: 'normal', lineHeight: 1.35 }} title={fullLabel}>
            {fullLabel}
          </Text>
        );
      }}
    />
  );
}
